import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

const HOLD_MS = 450;
const MOVE_TOLERANCE_PX = 10;

export interface LongPressHandlers {
  readonly pressing: boolean;
  readonly onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerUp: () => void;
  readonly onPointerCancel: () => void;
  readonly onClickCapture: (event: ReactMouseEvent<HTMLElement>) => void;
  readonly onContextMenu: (event: ReactMouseEvent<HTMLElement>) => void;
}

/**
 * Opens an action after a press is held still. A small move cancels it so
 * scrolling the thread does not count as a hold.
 */
export function useLongPress(onLongPress: () => void, enabled = true): LongPressHandlers {
  const [pressing, setPressing] = useState(false);
  const timerRef = useRef<number | null>(null);
  const originRef = useRef<{ x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);
  const onLongPressRef = useRef(onLongPress);
  onLongPressRef.current = onLongPress;

  function clearTimer(): void {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  useEffect(() => clearTimer, []);

  function finishHold(): void {
    clearTimer();
    originRef.current = null;
    setPressing(false);
  }

  function trigger(): void {
    finishHold();
    suppressClickRef.current = true;
    onLongPressRef.current();
  }

  return {
    pressing,
    onPointerDown(event) {
      if (!enabled || event.button !== 0) {
        return;
      }
      const target = event.target;
      if (target instanceof Element && target.closest('[data-long-press-ignore]') !== null) {
        return;
      }
      originRef.current = { x: event.clientX, y: event.clientY };
      setPressing(true);
      clearTimer();
      timerRef.current = window.setTimeout(trigger, HOLD_MS);
    },
    onPointerMove(event) {
      const origin = originRef.current;
      if (origin === null) {
        return;
      }
      const dx = event.clientX - origin.x;
      const dy = event.clientY - origin.y;
      if (dx * dx + dy * dy > MOVE_TOLERANCE_PX * MOVE_TOLERANCE_PX) {
        finishHold();
      }
    },
    onPointerUp: finishHold,
    onPointerCancel: finishHold,
    onClickCapture(event) {
      if (!suppressClickRef.current) {
        return;
      }
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
    onContextMenu(event) {
      if (!enabled) {
        return;
      }
      const target = event.target;
      if (target instanceof Element && target.closest('[data-long-press-ignore]') !== null) {
        return;
      }
      event.preventDefault();
      trigger();
    },
  };
}
